import { supabase } from './supabaseClient';

export interface InGameWalletBalance {
  symbol: string;
  name: string;
  balance: string;
  available: string;
  reserved: string;
  balanceBaseUnits: string;
  decimals: number;
}

const IN_GAME_WALLET_CACHE_TTL = 30_000;
const inGameWalletMemoryCache = new Map<string, { timestamp: number; data: InGameWalletBalance[] }>();

const getCacheKey = (userId: string) => `bubble-wallet-cache:${userId}`;

const readCache = (userId: string): InGameWalletBalance[] | null => {
  const now = Date.now();
  const memoryCache = inGameWalletMemoryCache.get(userId);
  if (memoryCache && now - memoryCache.timestamp < IN_GAME_WALLET_CACHE_TTL) {
    return memoryCache.data;
  }

  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const cached = window.localStorage.getItem(getCacheKey(userId));
    if (!cached) return null;
    const parsed = JSON.parse(cached) as { timestamp: number; data: InGameWalletBalance[] };
    if (!parsed?.timestamp || !parsed?.data) return null;
    if (now - parsed.timestamp > IN_GAME_WALLET_CACHE_TTL) return null;
    inGameWalletMemoryCache.set(userId, parsed);
    return parsed.data;
  } catch (error) {
    console.warn('⚠️ [InGameWallet] Failed to read cache:', error);
    return null;
  }
};

const writeCache = (userId: string, data: InGameWalletBalance[]) => {
  const payload = { timestamp: Date.now(), data };
  inGameWalletMemoryCache.set(userId, payload);

  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(getCacheKey(userId), JSON.stringify(payload));
  } catch (error) {
    console.warn('⚠️ [InGameWallet] Failed to write cache:', error);
  }
};

const formatWithThousandsSeparator = (value: string) => value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

// 格式化 base_units 為可讀格式
export const formatBaseUnits = (
  baseUnits: string | number | null | undefined,
  decimals: number
): string => {
  const normalized = typeof baseUnits === 'string' ? baseUnits : String(baseUnits ?? '0');
  if (!normalized) return '0.00';

  const isNegative = normalized.startsWith('-');
  const integerOnly = normalized.replace('-', '').split('.')[0];
  const digits = integerOnly.replace(/^0+(?=\d)/, '') || '0';

  if (decimals <= 0) {
    const formatted = formatWithThousandsSeparator(digits);
    return isNegative ? `-${formatted}` : formatted;
  }

  const padded = digits.padStart(decimals + 1, '0');
  const integerPart = padded.slice(0, -decimals) || '0';
  const fractionPart = padded.slice(-decimals);
  const displayFraction = fractionPart.slice(0, 2).padEnd(2, '0');
  const formattedInteger = formatWithThousandsSeparator(integerPart);
  const result = `${formattedInteger}.${displayFraction}`;

  return isNegative ? `-${result}` : result;
};

const stripDecimals = (value: string): string => (value || '0').split('.')[0] || '0';

// 計算可用餘額
export const calculateAvailableBalance = (balance: string, reserved: string) => {
  const balanceValue = BigInt(stripDecimals(balance));
  const reservedValue = BigInt(stripDecimals(reserved));
  const available = balanceValue - reservedValue;
  return available > 0n ? available.toString() : '0';
};

// 獲取用戶的所有遊戲內錢包餘額
export const getUserInGameWallets = async (
  userId: string,
  options?: { forceRefresh?: boolean }
): Promise<InGameWalletBalance[]> => {
  if (!options?.forceRefresh) {
    const cached = readCache(userId);
    if (cached) return cached;
  }

  const { data: walletRows, error: walletError } = await supabase
    .schema('api')
    .rpc('get_my_in_game_wallet_balances_v1');

  if (walletError) {
    throw walletError;
  }

  const walletBalances = (walletRows ?? []).filter((wallet) =>
    ['BBG', 'WLD', 'BBT'].includes(wallet.symbol)
  );

  const sortOrder = ['BBG', 'WLD', 'BBT'];
  const balances = walletBalances
    .sort((a, b) => sortOrder.indexOf(a.symbol) - sortOrder.indexOf(b.symbol))
    .reduce<InGameWalletBalance[]>((acc, config) => {
      const balanceBaseUnits = config.balance_base_units;
      const reservedBaseUnits = config.reserved_base_units;
      const availableBaseUnits = config.available_base_units;

      acc.push({
        symbol: config.symbol,
        name: config.name,
        balance: formatBaseUnits(balanceBaseUnits, config.decimals),
        available: formatBaseUnits(availableBaseUnits, config.decimals),
        reserved: formatBaseUnits(reservedBaseUnits, config.decimals),
        balanceBaseUnits,
        decimals: config.decimals,
      });
      return acc;
    }, []);

  writeCache(userId, balances);
  return balances;
};
